import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faMinus, faFilter, faCircleInfo, faXmark } from "@fortawesome/free-solid-svg-icons";

/**
 * FilterDrawer — reusable right-side filter panel.
 *
 * Props:
 *   isOpen        bool
 *   onClose       () => void
 *   filters       [{ label: string, key: string, options: [{ label, value }] }]
 *   appliedFilters  { [key]: string[] }   — current active filters from parent
 *   onApply       (filters: { [key]: string[] }) => void
 */
const FilterDrawer = ({ isOpen, onClose, filters = [], appliedFilters = {}, onApply }) => {
  const [expanded, setExpanded] = useState({});
  const [pending, setPending] = useState({});

  // Sync pending state whenever the drawer opens
  useEffect(() => {
    if (isOpen) {
      setPending(appliedFilters);
    }
  }, [isOpen]);

  const toggleSection = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleOption = (key, value) => {
    setPending((prev) => {
      const current = prev[key] || [];
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [key]: updated };
    });
  };

  const removeFilter = (key, value) => {
    setPending((prev) => {
      const updated = (prev[key] || []).filter((v) => v !== value);
      return { ...prev, [key]: updated };
    });
  };

  const clearAll = () => setPending({});

  const activeEntries = Object.entries(pending).flatMap(([key, values]) =>
    values.map((value) => ({ key, value }))
  );

  const handleApply = () => {
    onApply(pending);
    onClose();
  };

  const handleCancel = () => {
    setPending(appliedFilters);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={handleCancel}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-[420px] bg-white shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Body */}
        <div className="flex flex-col flex-1 overflow-y-auto p-8 gap-6">
          <h2 className="!font-poppins !font-bold !text-[24px] !text-gray-900 !m-0">
            Filters
          </h2>

          {/* Active filters */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="!text-sm !font-semibold text-gray-700">
                Active filters
              </span>
              {activeEntries.length > 0 && (
                <button
                  onClick={clearAll}
                  className="flex items-center gap-1.5 border border-gray-300 rounded-full px-3 py-1 !text-xs text-gray-600 hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={faFilter} size="xs" />
                  Clear all filters
                </button>
              )}
            </div>

            {activeEntries.length === 0 ? (
              <p className="!text-sm text-gray-400">No active filters.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {activeEntries.map(({ key, value }) => {
                  const filterDef = filters.find((f) => f.key === key);
                  const optionDef = filterDef?.options.find((o) => o.value === value);
                  const displayLabel = filterDef?.label || key;
                  const displayValue = optionDef?.label || value;
                  return (
                    <div
                      key={`${key}-${value}`}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <FontAwesomeIcon icon={faCircleInfo} className="text-gray-400" size="sm" />
                      <span>
                        <span className="font-medium">{displayLabel}:</span>{" "}
                        {displayValue}
                      </span>
                      <button
                        onClick={() => removeFilter(key, value)}
                        className="ml-auto text-gray-400 hover:text-gray-600 bg-transparent border-none outline-none"
                      >
                        <FontAwesomeIcon icon={faXmark} size="sm" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <hr className="border-gray-200" />

          {/* Filter sections */}
          <div className="flex flex-col">
            {filters.map((filter, i) => (
              <div key={filter.key}>
                {i > 0 && <hr className="border-gray-200" />}
                <button
                  onClick={() => toggleSection(filter.key)}
                  className="w-full flex items-center justify-between py-3.5 bg-transparent border-none outline-none text-gray-800 hover:text-gray-900"
                >
                  <span className="!text-sm !font-medium">{filter.label}</span>
                  <FontAwesomeIcon
                    icon={expanded[filter.key] ? faMinus : faPlus}
                    size="sm"
                    className="text-gray-500"
                  />
                </button>

                {expanded[filter.key] && (
                  <div className="flex flex-col gap-2 pb-3 pl-1">
                    {filter.options.map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center gap-2.5 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={(pending[filter.key] || []).includes(option.value)}
                          onChange={() => toggleOption(filter.key, option.value)}
                          className="w-4 h-4 rounded border-gray-300 accent-blue cursor-pointer"
                        />
                        <span className="!text-sm text-gray-700">{option.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex gap-4">
          <button
            onClick={handleCancel}
            className="flex-1 py-2.5 rounded-full border border-gray-300 !text-sm text-gray-700 bg-white hover:bg-gray-50 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="flex-1 py-2.5 rounded-full bg-blue !text-sm text-white font-semibold hover:opacity-90 transition-opacity"
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
};

export default FilterDrawer;
